import customElement from "@/wc/registry/lib/decorator/customElement/index.js";
import type IFormInputTypeComponent from "@/wc/registry/types/form-input-type";
import * as dom from "@/wc/registry/lib/dom/index.js";
import StekzFormInputElement from "@/wc/registry/component/form-input-base/index.js";

// Not using a shadowRoot, to secretly make use of the external css
// utilized by c-form-input. (to avoids fetching the same css bundle
// multiple times per c-form-input-[type])
const componentName = "c-form-input-textarea";
@customElement(componentName, {
    style: fetch("/wc/registry/component/form-input-textarea/styles.css").then(
        (res) => res.text()
    ),
    template: fetch(
        "/wc/registry/component/form-input-textarea/template.html"
    ).then((res) =>
        res.text().then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return doc.querySelector(`[data-component=${componentName}]`);
        })
    ),
    shadowRoot: false,
})
export default class FormInputTextArea
    extends StekzFormInputElement
    implements IFormInputTypeComponent
{
    usedProps = [
        "value",
        "label",
        "placeholder",
        "required",
        "readOnly",
        "rows",
        "helperText",
        "horizontal",
        "appendValue",
    ];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    actions: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nodes: any;

    static get observedAttributes() {
        return ["data-value", "data-read-only", "data-append-value"];
    }

    constructor() {
        super();

        // Similar to what is being done in the StekzHTMLElement constructor,
        // check it out for its documentation.
        this.nodes = dom.NodesProxy(this);
    }

    // When the value attribute is changed, be sure to update the nested nodes
    // to reflect this change when they exist.
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === "data-value" && this.nodes.textarea) {
            this.nodes.textarea.value = newValue;
        }
        if (name === "data-read-only" && this.nodes.textarea) {
            this.readOnly = newValue;
        }
        if (name === "data-append-value" && this.nodes.textarea) {
            this.appendValue = newValue;
        }
    }

    handleEvent() {
        // Only listening to the 'change' event at the moment. When this happens,
        // the form input change is 'committed' to the form input.
        this.reset();
    }

    set appendValue(value: string) {
        if (value !== undefined) {
            if (this.querySelector(".append-prepend-wrapper")) {
                const childElement = this.querySelector(".append-element");
                childElement.innerText = value;
            } else {
                const appendWrapper = document.createElement("div");
                appendWrapper.classList.add("append-prepend-wrapper");
                const childElement = document.createElement("div");
                childElement.innerText = value;
                childElement.classList.add("append-element");
                childElement.classList.add("language-append");
                appendWrapper.appendChild(this.nodes.textarea);
                appendWrapper.appendChild(childElement);
                this.nodes.control.insertAdjacentElement(
                    "afterbegin",
                    appendWrapper
                );
            }
        }
    }

    set name(value: string) {
        this.dataset.name = value;
    }
    set placeholder(value: string) {
        if (this.nodes.textarea) {
            this.nodes.textarea.setAttribute("placeholder", value);
        }
    }
    set readOnly(value: string) {
        if (!this.nodes.textarea) {
            return;
        }

        if (value === undefined || value === "null") {
            this.nodes.textarea.removeAttribute("readonly");
        } else {
            this.nodes.textarea.setAttribute("readonly", "");
        }
    }
    set rows(value: string) {
        if (value !== undefined) {
            this.nodes.textarea.setAttribute("rows", value);
        }
    }
    set helperText(value: string) {
        if (this.nodes.textarea && value) {
            const errorElement = this.nodes["help-text"];

            const pElement = document.createElement("p");
            pElement.classList.add("help", "has-text-grey", "mb-0");
            pElement.innerText = value;

            errorElement.parentNode.insertBefore(pElement, errorElement);
        }
    }
    set horizontal(value: string) {
        if (value !== undefined) {
            this.nodes["field-wrapper"].classList.add("field-wrapper");
            this.nodes["label-wrapper"].classList.add("label-wrapper");
        }
    }

    set value(value: string | undefined) {
        if (value === undefined) {
            return;
        }

        // Sometimes this value is set before the template is rendered, in that
        // case, store the value in the attribute so it can be used later.
        this.dataset.value = value;

        if (this.nodes.textarea && value !== undefined) {
            this.nodes.textarea.value = value;
        }
    }
    get value(): string {
        let value = this.dataset.value;
        if (this.nodes.textarea) {
            value = this.nodes.textarea.value;
        }

        if (!value) {
            return null;
        }

        return value;
    }

    reset() {
        this.error = "";
    }

    showErrorIndicators(value: string) {
        const icon = this.nodes.control.querySelector(".icon");
        if (value) {
            this.nodes.control.setAttribute("class", "control has-icons-right");
            this.nodes.textarea.setAttribute("class", "textarea is-danger");
            if (!icon) {
                this.nodes.control.insertAdjacentHTML(
                    "beforeend",
                    `
          <span class="icon is-small is-right">
            <i class="fas fa-exclamation-triangle"></i>
          </span>
        `
                );
            }
        } else {
            this.nodes.textarea.setAttribute("class", "textarea");
            if (icon) {
                icon.remove();
            }
        }
    }

    updateValues() {
        this.usedProps.forEach((prop) => {
            this[prop] = this.dataset[prop];
        });
    }

    connectedCallback() {
        super.connectedCallback();
        this.updateValues();
        this.nodes.textarea.addEventListener("change", this);
    }

    disconnectedCallback() {
        this.nodes.textarea.removeEventListener("change", this);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-input-textarea": FormInputTextArea;
    }
}
